<!--
    A line the assistant wrote, with the addresses in it clickable.

    # Why it is a component and not a directive or a filter

    The splitting is data (`libs/linkify.ts`) and the drawing is a template, and keeping
    the two apart is the whole point: nothing here goes near `v-html`. The text is
    written by a language model over the hotel's own records, so it is the one input in
    this portal that must never be able to bring an element with it. Every segment is
    interpolated, which is to say escaped, exactly as `{{ }}` always was.

    # The three kinds of link, and why they are drawn differently

    An INTERNAL path is a screen in this portal and is followed with the router — a
    full page load in the middle of a conversation would throw the conversation away.
    It is also the only kind that is checked before it is drawn: the model writes the
    path from its own idea of the address book, and a path the router cannot resolve is
    left as plain text rather than offered as a link to a blank screen.

    An EXTERNAL address opens in a new tab, for the same reason a guest profile does
    (GuestConflictAlert.vue): whoever is reading has a half-typed question in front of
    them. It is shortened for reading and carries the whole address in `title`, and it
    is the only kind with a copy button — the guest link an offer was issued under is
    read in order to be SENT to somebody, and selecting forty characters of a chat
    bubble with a mouse is what this replaces.

    A MAIL address is neither: it is handed to the machine's own mail client.
-->
<template>
    <div class="flex flex-col gap-2">
        <template v-for="(block, b) in blocks" :key="b">
            <ul v-if="block.kind === 'bullets'" class="list-disc space-y-1 ps-5">
                <li v-for="(item, i) in block.items" :key="i"><AssistantRuns :runs="item" :resolves="resolves" @copy="copy" /></li>
            </ul>
            <ol v-else-if="block.kind === 'numbers'" class="list-decimal space-y-1 ps-5">
                <li v-for="(item, i) in block.items" :key="i"><AssistantRuns :runs="item" :resolves="resolves" @copy="copy" /></li>
            </ol>
            <p v-else-if="block.kind === 'heading'" class="font-semibold text-highlighted">
                <AssistantRuns :runs="block.items[0]" :resolves="resolves" @copy="copy" />
            </p>
            <p v-else class="whitespace-pre-wrap">
                <template v-for="(line, i) in block.items" :key="i">
                    <br v-if="i > 0">
                    <AssistantRuns :runs="line" :resolves="resolves" @copy="copy" />
                </template>
            </p>
        </template>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRouter } from 'vue-router'

    import AssistantRuns from '@/components/AssistantRuns.vue'
    import { parseMarkdown, type IBlock } from '@/libs/markdown'
    import { PrintError, PrintSuccess } from '@/libs/toast'

    const props = defineProps<{ text: string }>()

    const { t }  = useI18n()
    const router = useRouter()

    /** Whether this portal actually has the screen the model named. */
    const resolves = (path: string): boolean => {
        try {
            return router.resolve(path).matched.length > 0
        } catch {
            // resolve() throws on a path it cannot even parse. That is an answer.
            return false
        }
    }

    const blocks = computed((): IBlock[] => parseMarkdown(props.text))

    const copy = async (url: string) => {
        try {
            await navigator.clipboard.writeText(url)
            PrintSuccess(t('assistant.link_copied'))
        } catch {
            // A clipboard the browser withheld — an insecure origin, or a permission
            // the person refused. The address is on screen and in `title`, so saying so
            // is the whole of the repair.
            PrintError(t('assistant.link_copy_failed'))
        }
    }
</script>
